import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Check, X } from "@phosphor-icons/react";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY = {
  supplier: "",
  entry_date: todayIso(),
  amount: "",
  comment: "",
  payment_method: "",
  execution_date: "",
  source: "",
  created_by: "",
};

export default function BalanceDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  paymentMethods,
  sources,
  people,
}) {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        supplier: initial.supplier || "",
        entry_date: initial.entry_date || todayIso(),
        amount: initial.amount ?? "",
        comment: initial.comment || "",
        payment_method: initial.payment_method || "",
        execution_date: initial.execution_date || "",
        source: initial.source || "",
        created_by: initial.created_by || "",
      });
    } else {
      setForm(EMPTY);
    }
    setTouched(false);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.supplier.trim()) e.supplier = "ΑΠΑΙΤΕΙΤΑΙ";
    if (!form.payment_method) e.payment_method = "ΑΠΑΙΤΕΙΤΑΙ";
    if (!form.source) e.source = "ΑΠΑΙΤΕΙΤΑΙ";
    if (!form.created_by) e.created_by = "ΑΠΑΙΤΕΙΤΑΙ";
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;
    const payload = {
      supplier: form.supplier.trim().toUpperCase(),
      entry_date: form.entry_date || null,
      amount: Number(form.amount || 0),
      comment: (form.comment || "").trim(),
      payment_method: form.payment_method,
      execution_date: form.execution_date || null,
      source: form.source,
      created_by: form.created_by,
    };
    onSave(payload, initial?.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        data-testid="balance-dialog"
        style={{
          background: "linear-gradient(180deg, #fdba74 0%, #fb923c 100%)",
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-black uppercase tracking-wider text-ink text-xl">
            {initial?.id
              ? "ΔΙΟΡΘΩΣΗ ΥΠΟΛΟΙΠΟΥ"
              : initial
              ? "ΑΝΤΙΓΡΑΦΟ ΥΠΟΛΟΙΠΟΥ"
              : "ΝΕΟ ΥΠΟΛΟΙΠΟ"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[70vh] overflow-auto pr-1">
          <Field label="ΠΡΟΜΗΘΕΥΤΗΣ" error={touched && errors.supplier}>
            <Input
              data-testid="balance-supplier"
              value={form.supplier}
              onChange={(e) => set("supplier", e.target.value.toUpperCase())}
              className="bg-white/85 uppercase font-bold text-base h-11"
              placeholder="ΟΝΟΜΑ ΠΡΟΜΗΘΕΥΤΗ"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="ΗΜΕΡΟΜΗΝΙΑ ΕΓΓΡΑΦΗΣ">
              <Input
                type="date"
                data-testid="balance-entry-date"
                value={form.entry_date || ""}
                onChange={(e) => set("entry_date", e.target.value)}
                className="bg-white/85 text-base h-11"
              />
            </Field>
            <Field label="ΠΟΣΟ (€)">
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="balance-amount"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                className="bg-white/85 font-bold text-base h-11"
                placeholder="0.00"
              />
            </Field>
          </div>

          <Field label="ΤΡΟΠΟΣ ΠΛΗΡΩΜΗΣ *" error={touched && errors.payment_method}>
            <Select
              value={form.payment_method}
              onValueChange={(v) => set("payment_method", v)}
            >
              <SelectTrigger
                data-testid="balance-payment-method"
                className="bg-white/85 font-bold uppercase text-base h-11"
              >
                <SelectValue placeholder="ΕΠΙΛΕΞΕ ΤΡΟΠΟ" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((m) => (
                  <SelectItem key={m} value={m} className="font-bold uppercase text-base">
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="ΗΜΕΡΟΜΗΝΙΑ ΕΚΤΕΛΕΣΗΣ">
            <Input
              type="date"
              data-testid="balance-execution-date"
              value={form.execution_date || ""}
              onChange={(e) => set("execution_date", e.target.value)}
              className="bg-white/85 text-base h-11"
            />
          </Field>

          <Field label="ΣΧΟΛΙΟ">
            <textarea
              data-testid="balance-comment"
              value={form.comment}
              onChange={(e) => set("comment", e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-white/85 px-3 py-2 text-base uppercase font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="ΠΡΟΑΙΡΕΤΙΚΟ ΣΧΟΛΙΟ"
            />
          </Field>

          <div className="bg-white/40 rounded-md p-3 border border-ink/10">
            <div className="text-sm uppercase tracking-widest font-bold text-ink/70 mb-2">
              ΠΗΓΗ ΥΠΟΛΟΙΠΟΥ ΑΠΟ *
            </div>
            <div className="flex gap-2 flex-wrap" data-testid="balance-source-group">
              {sources.map((s) => (
                <CheckButton
                  key={s}
                  label={s}
                  active={form.source === s}
                  onClick={() => set("source", s)}
                  testid={`balance-source-${s}`}
                />
              ))}
            </div>
            {touched && errors.source ? (
              <div className="text-xs font-bold text-red-700 mt-1.5">
                ΕΠΙΛΕΞΕ ΠΗΓΗ
              </div>
            ) : null}
          </div>

          <div className="bg-white/40 rounded-md p-3 border border-ink/10">
            <div className="text-sm uppercase tracking-widest font-bold text-ink/70 mb-2">
              ΚΑΤΑΧΩΡΗΣΗ ΑΠΟ *
            </div>
            <div className="flex gap-2 flex-wrap" data-testid="balance-person-group">
              {people.map((p) => (
                <CheckButton
                  key={p}
                  label={p}
                  active={form.created_by === p}
                  onClick={() => set("created_by", p)}
                  testid={`balance-person-${p}`}
                />
              ))}
            </div>
            {touched && errors.created_by ? (
              <div className="text-xs font-bold text-red-700 mt-1.5">
                ΕΠΙΛΕΞΕ ΥΠΑΛΛΗΛΟ
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="balance-cancel-btn"
            className="px-5 py-2.5 rounded-full flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider border-2 bg-white/80 text-ink border-ink/15 hover:border-ink/40 active:scale-95"
          >
            <X size={16} weight="bold" />
            ΑΚΥΡΟ
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            data-testid="balance-save-btn"
            disabled={touched && !isValid}
            className="px-5 py-2.5 rounded-full flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider border-2 bg-ink text-white border-ink hover:bg-black active:scale-95 disabled:opacity-50"
          >
            <Check size={16} weight="bold" />
            ΚΑΤΑΧΩΡΗΣΗ
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, error }) {
  return (
    <div className="space-y-1">
      <div className="text-sm uppercase tracking-widest font-bold text-ink/70 flex items-center gap-2">
        {label}
        {error ? <span className="text-red-700">— {error}</span> : null}
      </div>
      {children}
    </div>
  );
}

function CheckButton({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 rounded-md text-sm font-bold uppercase tracking-wider border-2 transition-all flex items-center gap-1.5 ${
        active
          ? "bg-emerald-600 text-white border-emerald-700 shadow"
          : "bg-white/70 text-ink border-ink/20 hover:border-emerald-500"
      }`}
    >
      {active ? <Check size={13} weight="bold" /> : null}
      {label}
    </button>
  );
}
